import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Hover affordance for cards that are themselves a link/button. */
  interactive?: boolean;
  /** Lift off the page — use sparingly, borders do most of the work. */
  elevated?: boolean;
}

export function Card({ className, interactive, elevated, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'pap-card',
        elevated && 'shadow-pop',
        interactive &&
          'cursor-pointer transition-[border-color,background-color] duration-150 hover:border-border-strong hover:bg-elevated/40',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col gap-1 border-b border-border px-5 py-4', className)}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-sm font-semibold text-fg', className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted', className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-4', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 border-t border-border bg-elevated/30 px-5 py-3',
        className,
      )}
      {...props}
    />
  );
}

export interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  /** Small trend chip, e.g. "+3 this week". */
  trend?: ReactNode;
  className?: string;
}

/** Compact metric tile used on the dashboard and admin overview. */
export function StatCard({ label, value, hint, icon, trend, className }: StatCardProps) {
  return (
    <Card className={cn('px-5 py-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-subtle">{label}</p>
          <p className="pap-tnum text-2xl font-semibold text-fg">{value}</p>
          {hint && <p className="text-xs text-muted">{hint}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {icon && (
            <span className="flex size-8 items-center justify-center rounded-md bg-elevated text-subtle [&_svg]:size-4">
              {icon}
            </span>
          )}
          {trend}
        </div>
      </div>
    </Card>
  );
}
